import { buildSearchPayload } from '@civilgrid/ui/hooks';
import type { FilterGroupItem } from '@civilgrid/ui/utils';
import { useQuery } from '@tanstack/react-query';
import { fetchDataRefreshStatus } from '~/api/dataRefreshStatus';
import { toCalendarDateFilters } from './dateFilters';
import { toRawResearchStatusFilters } from './researchStatusFilters';

export type UseGetDataRefreshStatusParams = {
  organizationId: string;
  pageIndex?: number;
  pageSize?: number;
  sorting?: { id: string; desc: boolean }[];
  filters?: FilterGroupItem;
  globalFilter?: string;
  enabled?: boolean;
};

/**
 * Fetch the per-jurisdiction data refresh status list for the organization with
 * pagination, sorting, and filters (server-side via the SearchPayload contract).
 * Same TanStack Query defaults as useGetProjects (staleTime 5m, no refetch on window focus).
 */
export function useGetDataRefreshStatus(params: UseGetDataRefreshStatusParams) {
  const { organizationId, pageIndex = 0, pageSize = 25, sorting, filters, globalFilter, enabled = true } = params;

  const searchPayload = buildSearchPayload({
    pageIndex,
    pageSize,
    sorting,
    // Wire format: bare calendar dates (dateFilters.ts) and raw research statuses (researchStatusFilters.ts).
    filters: toRawResearchStatusFilters(toCalendarDateFilters(filters)),
    globalFilter,
  });

  return useQuery({
    queryKey: ['dataRefreshStatus', { organizationId, searchPayload }],
    queryFn: ({ signal }) => fetchDataRefreshStatus(organizationId, searchPayload, { signal }),
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: enabled && !!organizationId,
  });
}
